import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type FoodQueueRow } from "../lib/api";
import { PageHeader } from "../components/EmptyState";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { fmtTimeShort } from "../lib/format";

/**
 * Rough notes in, records out.
 *
 * Type the day as you would say it — "big bowl of pasta, ran 5k, weighed 86.2,
 * slept badly" — and the parser on the workstation files each part where it
 * belongs: food against the day's calories, the run into workouts, the number
 * into weigh-ins, the rest onto the day itself.
 */
export default function Notes() {
  const [text, setText] = useState("");
  const [queue, setQueue] = useState<FoodQueueRow[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api.foodQueue().then((r) => setQueue(r.queue)).catch(() => setQueue([]));
  }, []);
  useEffect(load, [load]);

  // Anything waiting is picked up within 15 minutes; look again while it waits.
  useEffect(() => {
    if (!queue?.some((q) => q.status === "pending")) return;
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [queue, load]);

  async function send() {
    const body = text.trim();
    if (body.length < 2) return;
    setNote(null);
    try {
      await api.queueFood(body);
      setText("");
      load();
    } catch (e) {
      setNote(e instanceof ApiError ? `Couldn't send it (API ${e.status})` : (e as Error).message);
    }
  }

  async function onRecorded(wav: Blob, seconds: number) {
    setNote(`Sending ${seconds}s recording…`);
    try {
      await api.queueAudio(wav, seconds);
      setNote(null);
      load();
    } catch (e) {
      setNote(e instanceof ApiError ? `Upload failed (API ${e.status})` : (e as Error).message);
    }
  }

  async function onPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setNote("Uploading photo…");
    try {
      await api.queueFoodPhoto(file, undefined, text.trim() || undefined);
      setText("");
      setNote(null);
      load();
    } catch (e) {
      setNote(e instanceof ApiError ? `Upload failed (API ${e.status})` : (e as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader title="Notes" eyebrow="Tell it about your day" />

      <div className="card-pad">
        <textarea
          className="input h-24 w-full"
          value={text}
          placeholder={"big bowl of pasta for lunch\nran 5k this morning, felt heavy\nweighed 86.2 before breakfast"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
          }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
          <button type="button" className="btn-secondary px-3" onClick={() => photoRef.current?.click()}>
            📷 Photo
          </button>
          <VoiceRecorder onRecorded={onRecorded} />
          <div className="flex-1" />
          <button type="button" className="btn-accent px-5 py-2" disabled={text.trim().length < 2} onClick={send}>
            Send
          </button>
        </div>
        <p className="mt-2 text-xs text-ink-subtle">
          Speak it, photograph it or type it: food, workouts, a weight, how hard the day was, how
          you slept — all in one note, in any order. A recording is transcribed on the PC, which
          hears food words far better than the phone does. Read every 15 minutes.
        </p>
        {note && <p className="mt-2 text-sm text-ink">{note}</p>}
      </div>

      <h2 className="section-title mt-8 mb-3">Sent</h2>
      {queue === null ? (
        <div className="text-ink-subtle">Loading…</div>
      ) : queue.length === 0 ? (
        <p className="text-sm text-ink-subtle">Nothing yet. Anything you send appears here with what it became.</p>
      ) : (
        <ul className="space-y-2">
          {queue.map((q) => (
            <li key={q.id} className="card card-pad">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0 whitespace-pre-wrap text-sm text-ink">
                  {q.kind === "photo" ? "📷 " : q.kind === "audio" ? "🎙 " : ""}
                  {q.transcript ?? q.text}
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs text-ink-subtle hover:text-loss"
                  onClick={() => api.unqueueFood(q.id).then(load)}
                >
                  ✕
                </button>
              </div>
              <div className="mt-1.5 text-xs text-ink-subtle">
                {fmtTimeShort(q.created_at.replace(" ", "T") + "Z")} ·{" "}
                {q.status === "pending" ? (
                  <span className="pill-neutral">waiting for the PC</span>
                ) : q.status === "failed" ? (
                  <span className="pill-warn">{q.error ?? "couldn't read it"}</span>
                ) : (
                  <span className="text-ink">{describe(q.applied_json)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Turns what was applied into a sentence: "3 foods, 1 workout, 86.2 kg". */
function describe(applied: string | null | undefined): string {
  if (!applied) return "logged";
  let a: Record<string, unknown>;
  try {
    a = JSON.parse(applied);
  } catch {
    return "logged";
  }
  const parts: string[] = [];
  if (typeof a.food === "number") parts.push(`${a.food} food ${a.food === 1 ? "item" : "items"}`);
  if (typeof a.exercise === "number") parts.push(`${a.exercise} ${a.exercise === 1 ? "workout" : "workouts"}`);
  if (typeof a.weight_kg === "number") parts.push(`${a.weight_kg} kg`);
  if (typeof a.day_type === "string") parts.push(`${a.day_type} day`);
  if (typeof a.note === "string") parts.push("note kept");
  return parts.length ? parts.join(" · ") : "nothing to log";
}
